import { getEmitter } from '../../utils/emitter';
import { getDecodedIdToken, getIdToken, isAuthenticated } from '../../ciam-sdk';
import { ReconnectingWebSocket } from '@nktkas/rews';
import { BroadcastChannel, createLeaderElection, LeaderElector } from 'broadcast-channel';
import { FailoverMessage, OutgoingServerMessage, SessionState, CrossTabSyncMessage, IncomingServerMessage } from './types';

interface ReconnectOptions {
  baseDelayMs?: number;
  maxDelayMs?: number;
  jitterRatio?: number;
  maxRetries?: number;
}

interface SessionManagerOptions {
  sessionManagerEndpoint: string;
  clientId: string;
  pingInterval?: number;
  reconnect?: ReconnectOptions;
}

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30_000;
const RECONNECT_JITTER_RATIO = 0.3;
const RECONNECT_MAX_RETRIES = 10; // ~3.5min worst case (1+2+4+8+16 + 5×30s)
const TERMINAL_CLOSE_CODES = new Set([1003, 1008]);

export class SessionManager {
  private ws: ReconnectingWebSocket | null = null;
  private readonly sessionManagerEndpoint: string;
  private readonly clientId: string;

  private _isInitialized = false;

  private sessionState: SessionState | null = null;

  private readonly pingInterval: number;
  private pingIntervalId: ReturnType<typeof setInterval> | null = null;

  private readonly reconnectBaseDelayMs: number;
  private readonly reconnectMaxDelayMs: number;
  private readonly reconnectJitterRatio: number;
  private readonly reconnectMaxRetries: number;

  constructor(options: SessionManagerOptions) {
    this.sessionManagerEndpoint = options.sessionManagerEndpoint;
    this.clientId = options.clientId;
    this.pingInterval = options.pingInterval ?? 30000;
    this.reconnectBaseDelayMs = options.reconnect?.baseDelayMs ?? RECONNECT_BASE_DELAY_MS;
    this.reconnectMaxDelayMs = options.reconnect?.maxDelayMs ?? RECONNECT_MAX_DELAY_MS;
    this.reconnectJitterRatio = options.reconnect?.jitterRatio ?? RECONNECT_JITTER_RATIO;
    this.reconnectMaxRetries = options.reconnect?.maxRetries ?? RECONNECT_MAX_RETRIES;
  }

  async connect(): Promise<void> {
    if (this._isInitialized) return;

    await this.establishWebSocketConnection();

    this._isInitialized = true;
  }

  startMediaConsumption(): void {
    this.ws.send(JSON.stringify({ type: 'startMediaConsumption' } satisfies OutgoingServerMessage));
  }

  endMediaConsumption(): void {
    this.ws.send(JSON.stringify({ type: 'endMediaConsumption' } satisfies OutgoingServerMessage));
  }

  private applyState(state: SessionState) {
    if (state) {
      if (state.isInConcurrencyLimitedContext && !state.isActive) {
        getEmitter().emit('session:blocked');
      } else {
        getEmitter().emit('session:unblocked');
      }
    }
    this.sessionState = state;
  }

  private async establishWebSocketConnection(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    if (!isAuthenticated(this.clientId)) {
      throw new Error('User must be authenticated before initiating a session manager connection');
    }

    const urlProvider = () => {
      const url = new URL(`wss://${this.sessionManagerEndpoint}`);
      url.pathname = '/join';
      url.searchParams.set('token', getIdToken(this.clientId));
      return url.toString();
    };

    this.ws = new ReconnectingWebSocket(urlProvider, ['v1.mediahuis.session-manager+json'], {
      maxRetries: this.reconnectMaxRetries,
      reconnectionDelay: (attempt) => {
        const base = Math.min(this.reconnectBaseDelayMs * 2 ** attempt, this.reconnectMaxDelayMs);
        return Math.floor(base * (1 - this.reconnectJitterRatio + Math.random() * 2 * this.reconnectJitterRatio));
      },
    });

    this.ws.addEventListener('terminate', (event) => {
      if (event.detail.code === 'RECONNECTION_LIMIT') {
        getEmitter().emit('session:reconnect-exhausted');
      }
    });

    this.ws.onopen = () => {
      this.sessionState = null;
      this.startPing();
    };

    this.ws.onmessage = this.onWebsocketSessionMessage;

    this.ws.onclose = this.onWebsocketClose;

    this.ws.onerror = (error) => {
      console.error('Primary WebSocket error:', error);
    };
  }

  private reduceSessionStateMessage(prev: SessionState | null, message: IncomingServerMessage): SessionState | null {
    if (message.type !== 'sessionState') return prev;
    // WebSocket per-connection ordering guarantees last message received is the latest state.
    return message.data;
  }

  private startPing(): void {
    this.stopPing();
    this.pingIntervalId = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send('ping');
      }
    }, this.pingInterval);
  }

  private stopPing(): void {
    if (this.pingIntervalId !== null) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }
  }

  private onWebsocketSessionMessage = async (event: MessageEvent) => {
    if (event.data === 'pong') {
      return;
    }

    const message = JSON.parse(event.data) as IncomingServerMessage;

    this.sessionState = this.reduceSessionStateMessage(this.sessionState, message);

    this.applyState(this.sessionState);
  };

  private onWebsocketClose = async (event: CloseEvent) => {
    this.stopPing();

    if (TERMINAL_CLOSE_CODES.has(event.code)) {
      this.ws?.close();
      getEmitter().emit(event.code === 1008 ? 'session:policy-violation' : 'session:protocol-error', {
        code: event.code,
        reason: event.reason,
      });
    }
  };

  async shutdown(): Promise<void> {
    this.stopPing();

    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close(1000);
      }
      this.ws = null;
    }

    this.sessionState = null;
    this._isInitialized = false;
  }
}
